import { useState } from 'react';
import type React from 'react';
import type { InjectedRouter } from 'react-router';

import { FREE_PLAN_NAME } from '../../../../shared/subscriptions';
import { usePlanSelectorContext } from '../../../contexts/PlanSelectorContext';
import { useRouter } from '../../../contexts/RouterContext';
import { get } from '../../../redux/api';
import { isAuthed, nuc, openInNewTab } from '../../../utils';
import { apiSupportFeature } from '../../settings/billing/features/consts';
import type { MessageOptions} from '../../settings/billing/features/consts';
import type { ProjectDashboardRevision, RevisionRouteParams } from '../projectDashboardTypes';

export type SummaryActionsProps = {
  revision: ProjectDashboardRevision,
  params: RevisionRouteParams
};

export type SummaryActions = {
  onRebuild: (event: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>) => Promise<void>,
  onShowNoDependenciesModal: () => void,
  onHideNoDependenciesModal: () => void,
  onVisitAPIEndpoint: () => false | Window | null,
  routerPush: InjectedRouter['push'];
  isRebuilding: boolean,
  rebuildBuildId: number | null,
  shouldShowNoDependenciesModal: boolean,
  onHideModalStreamBuild: () => void,
};

const onVisitAPIEndpointForRevision = (
  revision: ProjectDashboardRevision,
  showPlanSelectorModal: (options: MessageOptions) => void,
) => {
  const locator = revision.data?.locator;
  if (!locator) {
    return false;
  }
  if (isAuthed()) {
    return openInNewTab(`/api/revisions/${nuc(locator)}`);
  }
  //  Show upgrade/no access modal if not authenticated
  showPlanSelectorModal({ tier: FREE_PLAN_NAME, focusFeature: apiSupportFeature });
  return false;
};

const useSummaryActions: (_: SummaryActionsProps) => SummaryActions = ({ revision, params }) => {
  const { push: routerPush } = useRouter();
  const [isRebuilding, setIsRebuilding] = useState<boolean>(false);
  const [rebuildBuildId, setRebuildBuildId] = useState<number | null>(null);
  const [shouldShowNoDependenciesModal, setShouldShowNoDependenciesModal] = useState<boolean>(false);
  const { showPlanSelectorModal } = usePlanSelectorContext();

  const onRebuild = async (event: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>) => {
    event.preventDefault();
    setIsRebuilding(true);
    setRebuildBuildId(null);
    const revisionString = nuc(`${params.project_id}$${params.revision_id}`);
    const url = `/api/revisions/${revisionString}/rebuild`;
    const resp = await get<Array<{ id: number}>>(url);
    const { id } = resp?.data?.[0] ?? { id: null };
    setRebuildBuildId(id);
  };

  const onShowNoDependenciesModal = () => setShouldShowNoDependenciesModal(true);

  const onHideNoDependenciesModal = () => setShouldShowNoDependenciesModal(false);

  const onVisitAPIEndpoint = () => onVisitAPIEndpointForRevision(revision, showPlanSelectorModal);

  const onHideModalStreamBuild = () => {
    setIsRebuilding(false);
  };

  return {
    onRebuild,
    onShowNoDependenciesModal,
    onHideNoDependenciesModal,
    onVisitAPIEndpoint,
    routerPush,
    isRebuilding,
    rebuildBuildId,
    shouldShowNoDependenciesModal,
    onHideModalStreamBuild,
  };
};

export default useSummaryActions;
